import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTasksListStyles } from '../../../components/tasks/TasksList.styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  DEFAULT_PRIORITY_FILTER,
  DEFAULT_STATUS_FILTER,
  TApiTaskEntity,
  TaskPriorityType,
  TaskStatusType,
  TTaskListOption,
  TTasksState,
} from '../../../types/Tasks';
import { IAppState } from '../../../store/Store';
import { filterTasksList, getTasksList } from '../../../actions/TaskActions';
import { debounce } from 'lodash';
import PageContent from '../../../components/PageContent';
import { ContentPage } from '../../../types/Content';
import FormControl from '@mui/material/FormControl';
import { Chip, InputLabel, MenuItem, Select, Tab, Tabs, TextField } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import Box from '@mui/material/Box';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import List from '@mui/material/List';
import TaskListItem from '../../../components/tasks/TaskListItem';
import Typography from '@mui/material/Typography';

type FilterTabType = 'search' | 'status' | 'priority';

const loadSavedStatuses = (): TaskStatusType[] => {
  try {
    const saved = localStorage.getItem('tasks_filter_statuses');
    return saved ? JSON.parse(saved) : DEFAULT_STATUS_FILTER;
  } catch {
    return DEFAULT_STATUS_FILTER;
  }
};

const loadSavedPriorities = (): TaskPriorityType[] => {
  try {
    const saved = localStorage.getItem('tasks_filter_priorities');
    return saved ? JSON.parse(saved) : DEFAULT_PRIORITY_FILTER;
  } catch {
    return DEFAULT_PRIORITY_FILTER;
  }
};

const buildLabelsFromOptions = (
  options: TTaskListOption[] | undefined,
  fallback: Record<number, string>
): Record<number, string> => {
  const labels = { ...fallback };
  options?.forEach((option) => {
    if (option.type !== null && option.label) {
      labels[option.type] = option.label;
    }
  });
  return labels;
};

const FALLBACK_STATUS_LABELS: Record<number, string> = {
  [TaskStatusType.Todo]: 'To-do',
  [TaskStatusType.OnHold]: 'On hold',
  [TaskStatusType.InProgress]: 'In progress',
  [TaskStatusType.Resolved]: 'Resolved',
  [TaskStatusType.Invalid]: 'Invalid',
};

const FALLBACK_PRIORITY_LABELS: Record<number, string> = {
  [TaskPriorityType.Urgent]: 'Urgent',
  [TaskPriorityType.Important]: 'Important',
  [TaskPriorityType.Medium]: 'Medium',
  [TaskPriorityType.Low]: 'Low',
};

const TaskListPage: React.FC = (): JSX.Element => {
  const [activeFilterTab, setActiveFilterTab] = useState<FilterTabType>('search');
  const { t } = useTranslation();
  const classes = useTasksListStyles({});
  const dispatch = useDispatch();
  const tasksState: TTasksState = useSelector<IAppState, TTasksState>(
    state => state.tasksState
  );

  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<TaskStatusType[]>(loadSavedStatuses);
  const [selectedPriorities, setSelectedPriorities] = useState<TaskPriorityType[]>(loadSavedPriorities);
  const initialFilterApplied = useRef(false);

  useEffect(() => {
    dispatch(getTasksList());
  }, [dispatch]);

  useEffect(() => {
    if (tasksState.data && !initialFilterApplied.current) {
      initialFilterApplied.current = true;
      dispatch(filterTasksList(searchValue, selectedStatuses, selectedPriorities));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasksState.data]);

  const statusLabels = useMemo(
    () => buildLabelsFromOptions(tasksState.statusOptions, FALLBACK_STATUS_LABELS),
    [tasksState.statusOptions]
  );

  const priorityLabels = useMemo(
    () => buildLabelsFromOptions(tasksState.priorityOptions, FALLBACK_PRIORITY_LABELS),
    [tasksState.priorityOptions]
  );

  const debouncedFilter = useMemo(
    () =>
      debounce(
        (search: string, statuses: TaskStatusType[], priorities: TaskPriorityType[]) => {
          dispatch(filterTasksList(search, statuses, priorities));
        },
        100
      ),
    [dispatch]
  );

  const handleStatusChange = (event: SelectChangeEvent<TaskStatusType[]>) => {
    const newStatuses = event.target.value as TaskStatusType[];
    setSelectedStatuses(newStatuses);
    debouncedFilter(searchValue, newStatuses, selectedPriorities);
  };

  const handlePriorityChange = (event: SelectChangeEvent<TaskPriorityType[]>) => {
    const newPriorities = event.target.value as TaskPriorityType[];
    setSelectedPriorities(newPriorities);
    debouncedFilter(searchValue, selectedStatuses, newPriorities);
  };

  const allStatusValues = [
    TaskStatusType.Todo,
    TaskStatusType.OnHold,
    TaskStatusType.InProgress,
    TaskStatusType.Resolved,
    TaskStatusType.Invalid,
  ];

  const allPriorityValues = [
    TaskPriorityType.Urgent,
    TaskPriorityType.Important,
    TaskPriorityType.Medium,
    TaskPriorityType.Low,
  ];

  return (
    <>
      <PageContent pageType={ContentPage.TASKS} showAsParagraph={true} />

      {!tasksState.loading && (
        <>
          <Box display="flex" flexDirection="column">
            <Tabs
              value={activeFilterTab}
              onChange={(_event, newValue) => setActiveFilterTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab icon={<SearchIcon />} label={t('tasks.tab.filter')} value="search" />
              <Tab icon={<TuneIcon />} label={t('tasks.tab.status')} value="status" />
              <Tab icon={<PriorityHighIcon />} label={t('tasks.tab.priority')} value="priority" />
            </Tabs>

            <Box mt={2}>
              {activeFilterTab === 'search' && (
                <TextField
                  value={searchValue}
                  id="task-search-filter"
                  label={t('tasks.search.label')}
                  placeholder={t('tasks.search.placeholder')}
                  className={classes.searchField}
                  onChange={e => {
                    const val = e.target.value;
                    setSearchValue(val);
                    debouncedFilter(val, selectedStatuses, selectedPriorities);
                  }}
                />
              )}

              {activeFilterTab === 'status' && (
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>{t('tasks.filter.status')}</InputLabel>
                  <Select
                    multiple
                    value={selectedStatuses}
                    onChange={handleStatusChange}
                    label={t('tasks.filter.status')}
                    renderValue={(selected) => (
                      <div className={classes.chips}>
                        {(selected as TaskStatusType[]).map((value) => (
                          <Chip
                            key={value}
                            label={statusLabels[value]}
                            size="small"
                            className={classes.chip}
                          />
                        ))}
                      </div>
                    )}
                  >
                    {allStatusValues.map((value) => (
                      <MenuItem key={value} value={value}>
                        {statusLabels[value]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {activeFilterTab === 'priority' && (
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel>{t('tasks.filter.priority')}</InputLabel>
                  <Select
                    multiple
                    value={selectedPriorities}
                    onChange={handlePriorityChange}
                    label={t('tasks.filter.priority')}
                    renderValue={(selected) => (
                      <div className={classes.chips}>
                        {(selected as TaskPriorityType[]).map((value) => (
                          <Chip
                            key={value}
                            label={priorityLabels[value]}
                            size="small"
                            className={classes.chip}
                          />
                        ))}
                      </div>
                    )}
                  >
                    {allPriorityValues.map((value) => (
                      <MenuItem key={value} value={value}>
                        {priorityLabels[value]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          </Box>

          <div className={classes.gridList}>
            <List>
              {tasksState.filteredList && tasksState.filteredList.length > 0 ? (
                tasksState.filteredList.map((task: TApiTaskEntity) => (
                  <TaskListItem key={task.id} task={task} />
                ))
              ) : (
                <Box py={2} textAlign="center">
                  <Typography variant="body2" color="textSecondary">
                    {t('tasks.no_results')}
                  </Typography>
                </Box>
              )}
            </List>
          </div>
        </>
      )}
    </>
  );
};

export default TaskListPage;
